// src/Components/SectorsSection.jsx
import React, { useState } from "react";

const DARK_GREEN    = "#1B3D2F";
const BRIGHT_GREEN  = "#3DBE5C";
const GOLD          = "#C9A53A";
const LIGHT_BG      = "#F2F2EE";
const CARD_BG       = "linear-gradient(145deg, #e8ede8, #d4e0d4)";
const CARD_HOVER_BG = "linear-gradient(145deg, #dde8dd, #c8d8c8)";

const sectors = [
  {
    id: 1,
    tag: "Guest Experience",
    title: "Hospitality",
    description:
      "From hotels and resorts to restaurants and events, we place service-driven professionals who elevate every guest experience.",
    bullets: ["Front-of-house & management", "F&B and events staffing", "Seasonal & peak-season support"],
  },
  {
    id: 2,
    tag: "Higher Education",
    title: "Academia",
    description:
      "Connecting universities, colleges, and research institutions with lecturers, researchers, and administrative talent.",
    bullets: ["Faculty & lecturing roles", "Research & academic support", "Institutional administration"],
  },
  {
    id: 3,
    tag: "Manufacturing & Mobility",
    title: "Automotive",
    description:
      "Supporting OEMs, dealerships, and component manufacturers with skilled technical, sales, and operations talent.",
    bullets: ["Technical & production staff", "Dealership & sales roles", "Supply chain & operations"],
  },
  {
    id: 4,
    tag: "Technical & Infrastructure",
    title: "Engineering",
    description:
      "Sourcing qualified engineers and technical specialists across civil, mechanical, and electrical disciplines for projects of every scale.",
    bullets: ["Civil, mechanical & electrical", "Project & site engineers", "Technical & specialist roles"],
  },
];

const SectorsSection = () => {
  const [hovered, setHovered] = useState(null);

  return (
    <section style={styles.section} aria-labelledby="sectors-heading">
      <style>{`
        @keyframes lift { from { transform: translateY(6px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
        @keyframes popGreen {
          0%   { transform: translateY(12px) scale(0.98); opacity: 0; }
          50%  { transform: translateY(-6px)  scale(1.02); opacity: 1; }
          100% { transform: translateY(0)     scale(1);    opacity: 1; }
        }
        .heading-animate { animation: popGreen 900ms cubic-bezier(.2,.9,.25,1) both; }
        @media (max-width: 980px) { .sectors-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 620px) { .sectors-grid { grid-template-columns: 1fr !important; } }
      `}</style>

      <div style={styles.container}>
        <header style={styles.header}>
          <h2 id="sectors-heading" className="heading-animate" style={styles.headingBig}>
            Sectors We Serve
          </h2>
        </header>

        <div className="sectors-grid" style={styles.grid}>
          {sectors.map((sector, idx) => {
            const isHovered = hovered === sector.id;
            return (
              <article
                key={sector.id}
                onMouseEnter={() => setHovered(sector.id)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  ...styles.card,
                  ...(isHovered ? styles.cardHovered : {}),
                  animation: `lift 420ms cubic-bezier(.2,.9,.25,1) both`,
                  animationDelay: `${idx * 60}ms`,
                }}
              >
                {/* Gold top accent bar */}
                <div style={{ ...styles.cardTopAccent, opacity: isHovered ? 1 : 0.5 }} />

                <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                  <div
                    style={{
                      ...styles.iconWrap,
                      color: isHovered ? GOLD : DARK_GREEN,
                      background: isHovered ? `rgba(201,165,58,0.15)` : "rgba(255,255,255,0.75)",
                    }}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <path d="M4 21V9l8-5 8 5v12" />
                      <path d="M9 21v-6h6v6" />
                    </svg>
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={styles.tagRow}>
                      <span style={{ ...styles.tag, color: isHovered ? GOLD : BRIGHT_GREEN }}>
                        {sector.tag}
                      </span>
                    </div>

                    <h3 style={styles.cardTitle}>{sector.title}</h3>
                    <p style={styles.cardDesc}>{sector.description}</p>

                    <ul style={styles.bullets}>
                      {sector.bullets.map((b, i) => (
                        <li key={i} style={styles.bulletItem}>
                          <span style={{ ...styles.bulletDot, background: isHovered ? GOLD : BRIGHT_GREEN }} />
                          <span style={{ color: DARK_GREEN }}>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: {
    position: "relative",
    padding: "56px 0",
    backgroundColor: LIGHT_BG,
    overflow: "visible",
  },
  container: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "0 20px",
    position: "relative",
    zIndex: 2,
  },
  header:     { textAlign: "center", marginBottom: 28 },
  headingBig: {
    fontSize: "clamp(28px, 6vw, 56px)",
    fontWeight: 900,
    margin: 0,
    letterSpacing: "-1px",
    color: DARK_GREEN,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 18,
  },
  card: {
    position: "relative",
    background: CARD_BG,
    borderRadius: 15,
    padding: 18,
    transition: "all 0.3s ease",
    overflow: "hidden",
    minHeight: 180,
    borderBottom: "4px solid transparent",
    boxShadow: "0 15px 35px rgba(27,61,47,0.12)",
  },
  cardHovered: {
    transform: "translateY(-8px)",
    boxShadow: "0 30px 50px rgba(27,61,47,0.20)",
    borderBottomColor: GOLD,
    background: CARD_HOVER_BG,
  },
  cardTopAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
    transition: "opacity 0.22s ease",
  },
  iconWrap: {
    width: 56,
    height: 56,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "all 0.2s",
    boxShadow: "0 8px 20px rgba(27,61,47,0.15)",
  },
  tagRow:    { display: "flex", gap: 8, alignItems: "center", marginBottom: 6 },
  tag:       { fontSize: 11, fontWeight: 700, textTransform: "uppercase" },
  cardTitle: { fontSize: 18, fontWeight: 800, color: DARK_GREEN, margin: "6px 0" },
  cardDesc:  { fontSize: 13, color: "#2e4a38", lineHeight: 1.6, margin: 0 },
  bullets:   { listStyle: "none", padding: 0, margin: "12px 0 0", display: "grid", gap: 8 },
  bulletItem:{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 },
  bulletDot: { width: 6, height: 6, borderRadius: 6, flexShrink: 0 },
};

export default SectorsSection;